import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectProductById, useDeleteProductMutation, useGetCartProductsQuery } from "../features/products/productsApiSlice";
import { useAddToCartMutation, useRemoveFromCartMutation } from "../features/users/usersApiSlice";
import useAuth from "../hooks/useAuth";
import { addToCart, removeFromCart, selectCartItems, rollbackCart, selectCartItemIds } from "../features/users/userSlice";
import { removeFromStore, rollbackStore } from "../features/products/productSlice";
import ClipLoader from "react-spinners/ClipLoader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { Skeleton } from "./ui/skeleton";
import { productImageUrlPrefix, ROUTES } from "../assets/data";
import { logDOM } from "@testing-library/react";
import { MdArrowDownward } from "react-icons/md";

const Product = ({ product }) => {
  // console.log(product);
  
  const productId = product?._id;
  
  const { id } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartItemIds = useSelector(selectCartItemIds);
  const isInCart = cartItemIds.includes(productId);
  
  const [addProductToCart] = useAddToCartMutation();
  const [removeFromCartMutation] = useRemoveFromCartMutation();

  const goToDetailedPage = () => {
    navigate(`${ROUTES.PRODUCT_DETAIL}/${productId}`, { state: { product } });
  };
  
  const onAddToCartClicked = async () => {
    // Optimistically update UI immediately
    dispatch(addToCart(product));
    
    try {
      // Handle backend operation in background
      await addProductToCart({ productId, userId: id }).unwrap();
    } catch (error) {
      // The rollback is now handled in the API slice's onQueryStarted
      console.error('Failed to add product to cart:', error);
    }
  };
  
  const onRemoveFromCartClicked = async () => {
    // Optimistically update UI immediately
    dispatch(removeFromCart(productId));
    
    try {
      // Handle backend operation in background
      await removeFromCartMutation({ productId, userId: id }).unwrap();
    } catch (error) {
      // The rollback is now handled in the API slice's onQueryStarted
      console.error('Failed to remove product from cart:', error);
    }
  };

  if (!product) {
    return (
      <div className="bg-white w-full min-w-[150px] max-w-[300px] shadow-md rounded-xl cursor-pointer hover:shadow-lg transition-shadow duration-75 ease-in-out flex flex-col justify-start items-start overflow-hidden text-xs xs:text-sm md:text-base space-y-2 p-2">
        <Skeleton className="w-full h-[150px] mb-5 rounded-t-xl" />
        <Skeleton className="h-4 w-[55%]" />
        <Skeleton className="h-3 w-[50%]" />
      </div>
    );
  }
  return (
    <div className="group bg-slate-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 h-full flex flex-col" onClick={() => goToDetailedPage()}>
      {/* Image Container */}
      <div className="relative w-full overflow-hidden rounded-t-lg" style={{ paddingBottom: "56.25%" }}>
        <img
          src={`${product.image[0]}`}
          alt="product visual display"
          className="absolute top-0 left-0 w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="w-full flex justify-between items-center px-4 py-[5px]">
        {/* Content Container */}
        <div className="h-full flex flex-col justify-center bg-red-20 select-none max-w-[80%]" onClick={() => goToDetailedPage()}>
              <h2 className="font-bold text-sm sm:text-[1.2rem] sm:text-base whitespace-nowrap overflow-hidden overflow-ellipsis md:max-w-[100%]">
                {product.product_name} - <span className="text-slate-500 text-[.8rem]">{product?.brand?.name}</span>
              </h2>
              <p className="text-[#007BFF] leading-5 text-xs sm:text-[.93rem] font-semibold flex items-center gap-1">
                $ {product.price || "__"} {product.need === "Request" && product.price && <MdArrowDownward className="text-slate-600 text-[.8rem]" />}
              </p>
        </div>

        {/* Bookmark Button - Positioned relative to card bottom */}
        <div className="flex justify-end -mt4 md:-mt5 mb- -2 z-20" onClick={() => goToDetailedPage()}>
          <button
            className={`${isInCart ? "material-icons" : "material-symbols-outlined"}
              w-7 h-7 min-h-7 md:w-9 md:h-9 md:min-h-9 
              flex items-center justify-center
              bg-gradient-to-br from-neutra-50 bg-blck/45 text-[#007BFF] rounded-full 
              hover:bg-slate-200 transition-colors duration-200
              text-[1.2rem] shadow-custom-shadowin`}
            onClick={(e) => {
              e.stopPropagation();
              if (isInCart) {
                onRemoveFromCartClicked();
              } else {
                onAddToCartClicked();
              }
            }}
          >
            bookmark
          </button>
        </div>
      </div>

    </div>
  );
  // else return null;
};

const ProductDetailed = ({ product, page }) => {
  // console.log(product);
  const productId = product?._id;
  console.log({productId});
  console.log({product});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useAuth();
  // const { refetch: refetchMyProducts } = useGetMyProductsQuery();
  const [deleteProduct, {
    isLoading,
    isSuccess,
    isError,
    error,
  }] = useDeleteProductMutation();
  const onDeleteProductClicked = async () => {
    // Optimistically update UI immediately
    dispatch(removeFromStore(product._id));
    
    try {
      // Handle backend operation in background
      await deleteProduct({ id: product._id });
    } catch (error) {
      // Only rollback if the backend operation fails
      console.error('Failed to remove product from store:', error);
      dispatch(rollbackStore());
    }
  }
  
  const [addProductToCart] = useAddToCartMutation();
  const [removeFromCartMutation] = useRemoveFromCartMutation();
  const cartItemIds = useSelector(selectCartItemIds);
  const isInCart = cartItemIds.includes(productId);

  const onRemoveFromCartClicked = async () => {
    // Optimistically update UI immediately
    dispatch(removeFromCart(productId));
    
    try {
      // Handle backend operation in background
      await removeFromCartMutation({ productId, userId: id }).unwrap();
    } catch (error) {
      // The rollback is now handled in the API slice's onQueryStarted
      console.error('Failed to remove product from cart:', error);
    }
  }

  const onAddToCartClicked = async () => {
    // Optimistically update UI immediately
    dispatch(addToCart(product));
    
    try {
      // Handle backend operation in background
      await addProductToCart({ productId, userId: id }).unwrap();
    } catch (error) {
      // The rollback is now handled in the API slice's onQueryStarted
      console.error('Failed to add product to cart:', error);
    }
  };

  useEffect(() => {
    if (isError){
      console.log(error?.data?.message);
    }
  }, [isError, error]);

  const goToEditForm = () => {
    navigate(`${ROUTES.EDIT_PRODUCT}/${productId}`);
  }
  const goToDetailedPage = () => {
    navigate(`${ROUTES.PRODUCT_DETAIL}/${productId}`, { state: { product } });
  }

  let content;
  if (product) {
    console.log(product);
    content = (
      <div className="bg-slate-200 px-5 py-4 xs:p-0 min-w-40 xs:h-[130px] rounded-md shadow-md w-full flex flex-col xs:flex-row gap-3 justify-start items-center relative my-1">
        <div className="w-full xs:h-full xs:w-auto flex-shrink-0 rounded-l-md">
          <img
            src={`${product?.image[0]}`}
            // src={`${productImageUrlPrefix}${product?.image[0]}`}
            alt={`${product.productName}'s pic`}
            className="w-full xs:w-auto xs:h-full object-contain cursor-pointer rounded-md xs:rounded-r-none"
            onClick={() => goToDetailedPage()}
          />
        </div>
        <div
          className="group w-full xs:flex-1 h-auto max-h-[90%] md:mb-0 flex justify-start items-start font-semibold cursor-pointer flex-wrap relative"
          onClick={() => goToDetailedPage()}
        >
          <div className="w-full text-[.9rem] text-neutral-500 font-sans leading-tight">
            {product.brand.name}{" "}
            <span className="font-normal text-[.9rem] text-slate-700">
              - {product.condition}
            </span>
          </div>
          <div className="w-full text-[1.2rem] font-bold font-sans leading-tight whitespace-pre-wrap overflow-hidden line-clamp-1 max-w-[95%]">
            {product.product_name}{" "}
          </div>
          <div
            className={`w-full text-[.8rem] pl-2 font-sans font-light text-slate-500 whitespace-pre-wrap overflow-hidden line-clamp-2 max-w-[95%]`}
          >
            {product.cover_detail}
          </div>
          <div className="w-full text-[1.2rem] text-[color:var(--COLOR)] sm:mt-0">
            $ {product.price}
          </div>
        </div>
          {page === "my-store" ? (
            <div className="absolute bottom-0 right-5 flex justify-start items-start">
              <button className="text-[.8rem] text-neutral-500 p-1 px-2 flex justify-center items-center hover:bg-blue-200 hover:text-blue-700 leading-none border-r border-neutral-300" onClick={() => goToEditForm()}><span className="material-icons text-[.8rem] mr-1 leading-none">edit</span> Edit</button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button className="text-[.8rem] text-neutral-500 p-1 px-2 flex justify-center items-center hover:bg-red-200 hover:text-red-500 leading-none"><span className="material-icons text-[.8rem] mr-1 leading-none">delete</span> Delete</button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-custom-content-bg">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete your product from our servers.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onDeleteProductClicked}>
                      {isLoading ? (
                        <ClipLoader
                          color= {"white"}
                          size={30}
                          aria-label="Loading Spinner"
                          data-testid="loader"
                        />
                      ) : (
                        "Continue"
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ) : page === "cart" || (page === "search" && isInCart) ? (
            <button className="text-[.8rem] bg-black sm:bg-transparent py-2 rounded-md sm:rounded-none w-full sm:w-auto md:text-neutral-500 p-1 px-2 flex justify-center items-center hover:bg-red-200 hover:text-red-500 leading-none sm:absolute sm:bottom-0 sm:right-5 text-white" onClick={() => onRemoveFromCartClicked()}><span className="material-icons text-[.8rem] mr-1 leading-none">delete</span> Remove</button>
          ) : (
            <button className="text-[.8rem] bg-black sm:bg-transparent py-2 rounded-md sm:rounded-none w-full sm:w-auto md:text-neutral-500 p-1 px-2 flex justify-center items-center hover:bg-green-300 hover:text-green-800 leading-none sm:absolute sm:bottom-0 sm:right-5 text-white" onClick={() => onAddToCartClicked()}><span className="material-icons text-[.8rem] mr-1 leading-none font-bold">add</span> Add to Cart</button>

          )}
      </div>
    );
    // }
  }else{
    content = (
      <div className="bg-white min-w-40 border-b border-neutral-200 w-[100%] pr-3 flex flex-col xs:flex-row justify-start items-center relative m-2">
        <div className="flex items-center space-x-4">
          <Skeleton className="h-20 w-32 rounded-md" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-[550px]" />
            <Skeleton className="h-4 w-[500px]" />
          </div>
        </div>
      </div>
    );
  }
  return content;
}

export { ProductDetailed, Product };
